import React, { ChangeEvent, Dispatch, FC, MouseEvent, SetStateAction, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import styled from "styled-components";

import { useSelector } from "react-redux";
import Button from "src/components/Button";
import { CustomCheckBox } from "src/pages/ClientDetails/ClientPortalTiles/SingleTile";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { ID } from "src/types";

type AddCommentSectionProps = {
  workOrderId: ID;
  setAddCommentSuccess: Dispatch<SetStateAction<boolean>>;
};
const AddCommentSection: FC<AddCommentSectionProps> = ({ workOrderId, setAddCommentSuccess }) => {
  const { isManager } = useSelector(getUserData);
  const [commentText, setCommentText] = useState<string>("");
  const [showButtonGroup, setShowButtonGroup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [clientViewable, setClientViewable] = useState(false);

  const checkBoxHandler = () => {
    setClientViewable((prev) => !prev);
  };

  const commentTextChangeHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
  };

  const resetButtonClickHandler = (e: MouseEvent<HTMLElement>) => {
    setShowButtonGroup(false);
    setCommentText("");
    setErrorMessage("");
    e.stopPropagation();
  };

  const commentSubmitHandler = async () => {
    if (loading || !commentText.trim()) {
      return;
    }

    try {
      setErrorMessage("");
      setLoading(true);
      const data = await FirebaseDataService.createWorklogComment(
        "",
        commentText.trim(),
        workOrderId,
        isManager,
        !clientViewable,
      );
      if (data.status === "success") {
        setCommentText("");
        return setAddCommentSuccess(true);
      }

      if (data.status === "error") {
        throw new Error(data.message);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Unable to add comment.");
    } finally {
      setLoading(false);
    }
  };

  const saveButtonClickHandler = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void commentSubmitHandler();
  };

  return (
    <CustomContainer>
      <Form.Control
        disabled={loading}
        as="textarea"
        rows={3}
        value={commentText}
        onChange={commentTextChangeHandler}
        onClick={(e: MouseEvent<HTMLElement>) => {
          setShowButtonGroup(true);
          e.stopPropagation();
        }}
        placeholder="Write a comment..."
      />
      {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
      {showButtonGroup && (
        <ButtonContainer>
          <Button
            disabled={loading || !commentText.trim()}
            text="Save"
            type="button"
            onClick={saveButtonClickHandler}
          />
          <Button
            disabled={loading}
            text="Cancel"
            variant="secondary"
            type="button"
            onClick={resetButtonClickHandler}
          />
          {isManager && (
            <CheckBoxWrapper>
              <Form.Check
                defaultChecked={clientViewable}
                className="custom-checkbox"
                type="checkbox"
                label="Hide comment from clients"
                onChange={checkBoxHandler}
              />
            </CheckBoxWrapper>
          )}
        </ButtonContainer>
      )}
    </CustomContainer>
  );
};

const ButtonContainer = styled.div`
  margin-top: 10px;
  display: flex;

  & > *:first-child {
    margin-right: 20px;
  }
`;

const CustomContainer = styled.div`
  margin-bottom: 20px;
`;

const CheckBoxWrapper = styled(CustomCheckBox)`
  padding-left: 15px;
  display: flex;
  align-items: center;
`;
export default AddCommentSection;
